import { useCallback, useEffect, useMemo, useState } from 'react';
import { Star } from 'lucide-react';
import api from '../../services/api';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import './d28d-admin-surfaces.css';

function StarInput({ value, onChange, disabled = false }) {
  return (
    <div className="inline-flex items-center gap-1" role="group">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          className={`p-0.5 ${disabled ? 'cursor-default' : 'hover:scale-110 transition'}`}
          onClick={() => !disabled && onChange?.(n)}
          aria-label={`${n} estrellas`}
        >
          <Star
            size={22}
            className={n <= (value || 0) ? 'fill-amber-400 text-amber-400' : 'text-stone-300'}
          />
        </button>
      ))}
    </div>
  );
}

function TrainerAvatar({ name, url }) {
  const src = url ? resolveMediaUrl(url) : null;
  const initials = String(name || '?').trim().slice(0, 1).toUpperCase();
  if (src) {
    return (
      <img
        src={src}
        alt={name || 'Entrenador'}
        className="w-16 h-16 rounded-full object-cover border border-slate-200 bg-slate-100"
      />
    );
  }
  return (
    <div className="w-16 h-16 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-xl font-bold border border-slate-300">
      {initials}
    </div>
  );
}

/**
 * Encuesta bloqueante día 25+ del ciclo D28D plataforma (no sedes).
 */
export default function D28dTrainerEvalGate({ enabled = true, onCompleted = null }) {
  const [payload, setPayload] = useState(null);
  const [scores, setScores] = useState({});
  const [comments, setComments] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!enabled) {
      setPayload(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get('/d28d/cycle/trainer-eval/me');
      const data = res.data?.data || null;
      setPayload(data);
      if (data?.required && data.trainers) {
        const nextScores = {};
        const nextComments = {};
        data.trainers.forEach((tr) => {
          nextScores[tr.host_key] = {};
          nextComments[tr.host_key] = '';
          (data.criteria || []).forEach((c) => {
            nextScores[tr.host_key][c.id] = '';
          });
        });
        setScores(nextScores);
        setComments(nextComments);
      }
    } catch {
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const required = Boolean(payload?.required);
  const criteria = payload?.criteria || [];
  const trainers = payload?.trainers || [];

  const setScore = (hostKey, criteriaId, value) => {
    setScores((prev) => ({
      ...prev,
      [hostKey]: { ...(prev[hostKey] || {}), [criteriaId]: value },
    }));
  };

  const canSubmit = useMemo(() => {
    if (!required) return false;
    return trainers.every((tr) => criteria.every((c) => {
      if (!c.required) return true;
      const v = Number(scores[tr.host_key]?.[c.id]);
      return Number.isFinite(v) && v >= 1 && v <= 5;
    }));
  }, [required, trainers, criteria, scores]);

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.post('/d28d/cycle/trainer-eval/me', {
        trainers: trainers.map((tr) => ({
          host_key: tr.host_key,
          scores: scores[tr.host_key] || {},
          comment: comments[tr.host_key] || '',
        })),
      });
      setPayload((prev) => (prev ? { ...prev, required: false, completed: true, reason: 'completed' } : prev));
      onCompleted?.();
    } catch (ex) {
      setErr(ex?.response?.data?.error || 'No se pudo enviar la evaluación');
    } finally {
      setBusy(false);
    }
  };

  if (!enabled || loading || !required) return null;

  return (
    <div
      className="fixed inset-0 z-[12000] bg-stone-100 text-stone-900 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="d28d-trainer-eval-title"
    >
      <div className="min-h-full max-w-3xl mx-auto px-4 py-6 sm:py-10 space-y-5">
        <header className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="h-2 bg-[var(--d28d-yellow,#eab308)]" />
          <div className="p-5 sm:p-6 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">
              D28D · Día {payload.cycle_day} de ciclo
            </p>
            <h1 id="d28d-trainer-eval-title" className="text-2xl font-bold text-stone-900">
              {payload.title || 'Encuesta de satisfacción D28D'}
            </h1>
            <p className="text-sm text-stone-600">
              Evalúa a cada entrenador activo de tu ciclo. Es obligatorio completar todas las preguntas
              con estrellas (1 poco satisfactorio · 5 muy satisfactorio) para continuar.
            </p>
            <p className="text-xs text-red-600">* Indica que la pregunta es obligatoria</p>
          </div>
        </header>

        {err ? (
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{err}</p>
        ) : null}

        {trainers.map((tr) => (
          <section key={tr.host_key} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4">
            <div className="flex items-center gap-3">
              <TrainerAvatar name={tr.host_name} url={tr.avatar_url} />
              <div>
                <h2 className="font-bold text-lg text-stone-900">{tr.host_name}</h2>
                {tr.host_email ? <p className="text-xs text-stone-500">{tr.host_email}</p> : null}
                <p className="text-xs text-stone-500 mt-0.5">
                  {tr.classes_count != null ? `${tr.classes_count} clase(s) en el ciclo` : 'Entrenador del ciclo'}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {criteria.map((c) => (
                <div key={`${tr.host_key}-${c.id}`} className="border border-slate-100 rounded-xl p-3 bg-slate-50">
                  <p className="text-sm font-semibold text-stone-800 mb-2">
                    {c.label}
                    {c.required ? <span className="text-red-600"> *</span> : null}
                  </p>
                  <StarInput
                    value={Number(scores[tr.host_key]?.[c.id]) || 0}
                    onChange={(n) => setScore(tr.host_key, c.id, n)}
                  />
                </div>
              ))}
            </div>

            <label className="block">
              <span className="text-xs font-semibold text-stone-600">Comentario (opcional)</span>
              <textarea
                className="ui-input mt-1 w-full min-h-[4rem] text-sm"
                value={comments[tr.host_key] || ''}
                onChange={(e) => setComments({ ...comments, [tr.host_key]: e.target.value })}
                placeholder="Tu respuesta"
              />
            </label>
          </section>
        ))}

        <div className="sticky bottom-0 bg-stone-100/95 backdrop-blur border-t border-stone-200 -mx-4 px-4 py-4 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canSubmit || busy}
            onClick={submit}
          >
            {busy ? 'Enviando…' : 'Enviar evaluación y continuar'}
          </button>
        </div>
      </div>
    </div>
  );
}
